<template>
  <Head :title="session.title" />

  <main class="beamer">
    <div class="legend">
      <dl>
        <div v-for="set in financialResults.datasets" :key="set.label">
          <dt>
            <span :style="{ backgroundColor: set.backgroundColor }"></span>
          </dt>
          <dd>{{ set.label }}</dd>
        </div>
      </dl>
    </div>

    <div class="chart" :style="chartStyle">
      <canvas ref="chart-canvas"></canvas>
    </div>
  </main>
</template>

<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import { useFullscreen, useIntervalFn } from '@vueuse/core'
import type { Chart as ChartType } from 'chart.js'
import Chart from 'chart.js/auto'
import { computed, onMounted, useTemplateRef, watch } from 'vue'

import Layout from '/@front:layouts/beamer.vue'

const { isFullscreen } = useFullscreen()

import type { GameSession, ScenarioClientWithNPS } from '/@front:types/shared'

defineOptions({
  layout: [Layout],
})

type ChartDataset = {
  label: string
  data: number[]
  backgroundColor: string
}

const props = defineProps<{
  session: GameSession
  clientsWithNps: ScenarioClientWithNPS
  financialResults: {
    labels: string[]
    datasets: ChartDataset[]
  }
}>()

useIntervalFn(() => {
  router.reload()
}, 10_000)

const chartRef = useTemplateRef<HTMLCanvasElement>('chart-canvas')

const chartStyle = computed(() => {
  const heightRatio = isFullscreen.value ? 0.65 : 0.65

  return {
    width: '100%',
    height: Math.round(window.innerHeight * heightRatio) + 'px',
  }
})

let chart: ChartType | undefined = undefined

watch(
  () => props.financialResults,
  () => {
    if (!props.financialResults) {
      return
    }

    if (!chart) {
      initChart()
      return
    }

    chart.data.labels = [...props.financialResults.labels]

    chart.data?.datasets.forEach((dataset, index) => {
      dataset.data = [...props.financialResults.datasets[index].data]
    })

    chart.update()
  },
)

const initChart = () => {
  if (!props.financialResults?.labels || !props.financialResults?.datasets) {
    return
  }

  chart = new Chart(chartRef.value as HTMLCanvasElement, {
    type: 'line',
    data: {
      labels: props.financialResults.labels,
      datasets: props.financialResults.datasets,
    },
    options: {
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
        },
      },
      scales: {
        y: {
          ticks: {
            // display: false,
            callback: (v) => `${v}M`,
          },
          grid: {
            display: true,
          },
          border: {
            display: false,
          },
        },
        x: {
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    },
  })
}

onMounted(() => {
  initChart()
})
</script>
